import { browser } from '$app/environment';
import type { FinancingOption } from '$lib/storyblok/types';
import { invalidatePrice, ReactiveProduct } from './reactiveProduct.svelte';

export interface CheckoutSessionData {
	previousLookupKey?: string | null | undefined;
	addon_unit_amount?: number;
	couponCode?: string;
	product?: ReactiveProduct;
	productBundle?: string;
	termsOfService?: string;
	afterSubmitText?: string;
	isTrial?: boolean;
	accessCode?: string;
	shippingId?: string;
	userLabels?: Set<string>;
	financingOptions?: FinancingOption;
	successPage?: string;
	etas?: {
		min?: number;
		max?: number;
	};
	adjustableQuantity?: {
		enabled?: boolean;
		max?: number;
	};
	productSubline?: string;
}

export const CHECKOUT_SESSION_STORAGE_KEY = 'mill_checkout_session';

export class CheckoutSessionStorage {
	static fetch(): CheckoutSessionData {
		if (!browser) return {};

		const rawData = localStorage.getItem(CHECKOUT_SESSION_STORAGE_KEY);
		if (!rawData) return {};

		const data: CheckoutSessionData = JSON.parse(rawData, (key, value) => {
			if (key === 'userLabels') {
				return new Set(value);
			}

			if (key === 'product' && typeof value === 'object' && 'price' in value) {
				const product = new ReactiveProduct(value.price.lookup_key, {
					price: value.price,
					promotionCode: value.promotionCode
				});

				if (!value.promotionCode) {
					invalidatePrice(value.price.lookup_key);
				}

				return product;
			}

			return value;
		});

		return data;
	}

	static store(data: CheckoutSessionData) {
		if (!browser) return;

		clearTimeout(this.storeTimeout);

		this.storeTimeout = setTimeout(() => {
			const storageData = {
				...data,
				userLabels: Array.from(data.userLabels || []),
				product: data.product
					? {
						price: data.product.price,
						promotionCode: data.product.promotionCode
					}
					: undefined
			};

			localStorage.setItem(CHECKOUT_SESSION_STORAGE_KEY, JSON.stringify(storageData));
		}, 300);
	}

	private static storeTimeout: ReturnType<typeof setTimeout>;
}
